import { makeObservable, observable, action, runInAction } from 'mobx';
import * as math from 'mathjs';
import { Source, ColumnType, RecipeColumnState, RecipeRowState } from '../types';

import type { GroupStore } from './GroupStore';
import type { FormulaScope } from './FormulaScope';
import { log } from '@/lib/utils/logger';

/**
 * Format a timestamp (number) or Date to a DateTime string
 * Matches Knockout's moment().format(DateFormat.dateTime.formatString)
 * Format: "MM/DD/YYYY hh:mm a" (no seconds, 12-hour format)
 */
function formatDateTime(value: number | Date | string): string {
  if (typeof value === 'string') return value; // Already formatted
  const date = typeof value === 'number' ? new Date(value) : value;

  // Format to MM/DD/YYYY hh:mm a
  return date
    .toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })
    .replace(',', '');
}

/**
 * MobX store for a single recipe row.
 *
 * Replaces Knockout's RecipeRowVM with MobX observables:
 * - Observable values map (like ko.observable for each cell)
 * - getValue() with automatic formula calculation (like ko.computed)
 * - Overwritable formulas tracked in overrides map (same as Knockout)
 *
 * Formulas are evaluated lazily when getValue() is called, and MobX
 * automatically tracks which observables were accessed.
 */
export class RowStore {
  id: number | undefined;
  symbol: string;
  name: string;
  /** Optional template row description; tooltip on row label cell only. */
  description?: string;
  definedInTemplate: boolean;
  showComment: boolean;
  comment: string;
  scrollTo: boolean;

  // Row-specific formulas for FormulaPerRow columns
  // Format: [["columnSymbol", "formula"], ...]
  formulas?: string[][];

  // Observable map of values (for manual input)
  // Using observable.map() for fine-grained reactivity (like Knockout's ko.observable)
  values = observable.map<string, any>();

  // Track which overwritable formulas have been manually changed
  overrides = new Map<string, boolean>();

  // Cache for formula results to prevent redundant calculations
  // Unlike MobX computed, this is a simple cache without automatic tracking
  private formulaCache = new Map<string, any>();

  // Observable flag to force re-renders when cache is cleared
  // This ensures MobX tracks cache invalidation even though the cache itself isn't observable
  cacheVersion = 0;

  // Track which formulas are currently being evaluated to detect circular dependencies
  // Unlike Knockout (which has ko.computed built-in protection), we need manual tracking
  private evaluating = new Set<string>();

  // Reference to parent group and formula scope
  private group: GroupStore;
  private formulaScope: FormulaScope;

  constructor(data: RecipeRowState, group: GroupStore, formulaScope: FormulaScope) {
    this.id = data.id;
    this.symbol = data.symbol;
    this.name = data.name;
    this.description = data.description;
    this.definedInTemplate = data.definedInTemplate;
    this.showComment = data.showComment;
    this.comment = data.comment || '';
    this.scrollTo = data.scrollTo;
    this.formulas = data.formulas; // Row-specific formulas for FormulaPerRow columns

    this.group = group;
    this.formulaScope = formulaScope;

    // Initialize values from data (observable.map() is already created)
    data.values.forEach((value, key) => {
      this.values.set(key, value);
    });

    // Knockout RecipeRowVM.name reads/writes the "name" column — keep row.name in sync on load.
    this.syncNameFromValues();

    makeObservable(this, {
      values: observable,
      overrides: observable,
      name: observable,
      showComment: observable,
      comment: observable,
      scrollTo: observable,
      cacheVersion: observable, // Track cache invalidation for MobX reactivity
      setValue: action,
      clearOverride: action,
      clearValues: action,
      setComment: action,
    });

    // NOTE: We DON'T use MobX computed() for formulas to avoid circular dependency issues
    // Instead, we cache formula results manually and rely on observer to track raw value dependencies
  }

  /**
   * Get value for a column.
   *
   * Different from Knockout (which creates ko.computed upfront):
   * - Manual columns: return stored value from observable.map
   * - Formula columns: calculate with simple caching (no MobX computed)
   * - Overwritable formulas: check override first
   * - StartingMaterial columns: return pre-populated value from sample
   *
   * MobX observer will track dependencies through this.values access.
   * Simple cache prevents redundant calculations without computed() cycles.
   */
  getValue(columnSymbol: string): any {
    const column = this.group.getColumn(columnSymbol);
    if (!column) {
      return undefined;
    }

    // For startingMaterial columns, just return the pre-populated value (read-only)
    if (column.sourceType === Source.StartingMaterial) {
      return this.values.get(columnSymbol);
    }

    // For overwritable formulas, check if manually overridden
    if (column.sourceType === Source.OverwrittableFormula && this.overrides.get(columnSymbol)) {
      return this.values.get(columnSymbol);
    }

    // For FormulaPerRow, check if this row has a custom formula
    if (column.sourceType === Source.FormulaPerRow) {
      const rowFormula = this.getRowFormula(columnSymbol);
      if (rowFormula) {
        // Check cache first
        if (this.formulaCache.has(columnSymbol)) {
          return this.formulaCache.get(columnSymbol);
        }

        // Evaluate row-specific formula
        const result = this.evaluateRowFormula(rowFormula, columnSymbol);
        this.formulaCache.set(columnSymbol, result);
        return result;
      }
      // No row-specific formula - return undefined or default
      return undefined;
    }

    // For autoincrement columns, compute value based on row position in group
    if (column.sourceType === Source.Autoincrement) {
      const rowIndex = this.group.rows.indexOf(this);
      // First row (index 0) = 1, second row (index 1) = 2, etc.
      return rowIndex >= 0 ? rowIndex + 1 : undefined;
    }

    // For regular formulas, use cache to avoid redundant calculations
    if (column.sourceType === Source.Formula || column.sourceType === Source.OverwrittableFormula) {
      // Check cache first
      if (this.formulaCache.has(columnSymbol)) {
        return this.formulaCache.get(columnSymbol);
      }

      // Detect circular dependencies - indicates BROKEN TEMPLATE
      // This should NEVER fire if excludeSymbol is working correctly for same-column refs
      // If it fires, it means: real circular dependency (A→B→A) or cross-row circular (row1.x→row2.y→row1.x)
      if (this.evaluating.has(columnSymbol)) {
        log.error(
          `[RowStore] CIRCULAR DEPENDENCY DETECTED in template!`,
          `Formula "${columnSymbol}" references itself directly or indirectly.`,
          `Evaluation stack: [${Array.from(this.evaluating).join(' → ')} → ${columnSymbol}]`,
          `Group: ${this.symbol}, Row: ${this.symbol}`
        );
        return undefined; // Prevent infinite loop
      }

      // Mark as evaluating
      this.evaluating.add(columnSymbol);

      try {
        const result = this.calculateFormula(column);
        this.formulaCache.set(columnSymbol, result);
        return result;
      } finally {
        // Always clean up, even if error occurs
        this.evaluating.delete(columnSymbol);
      }
    }

    // Otherwise return the stored value (manual input from observable.map)
    return this.values.get(columnSymbol);
  }

  /**
   * Get value for use in formulas.
   *
   * Direct port from Knockout's RecipeRowVM.getValueForCalculations():
   * - DateTime → milliseconds (for duration calculations)
   * - Empty string in Number → undefined (not 0)
   * - Values starting with "=" → inline formula evaluation
   *
   * This ensures formulas behave identically to the Knockout client.
   */
  getValueForCalculations(columnSymbol: string): any {
    const column = this.group.getColumn(columnSymbol);
    if (!column) {
      return undefined;
    }

    const value = this.getValue(columnSymbol);

    // DateTime: convert to milliseconds for calculations
    if (column.type === ColumnType.DateTime && value) {
      return new Date(value).getTime();
    }

    // Number: handle empty string and inline formulas
    if (column.type === ColumnType.Number) {
      // Empty string should be undefined, not 0
      if (value === '' || value === null || value === undefined) {
        return undefined;
      }
      // Support inline formulas starting with "="
      if (typeof value === 'string' && value.startsWith('=')) {
        try {
          return math.evaluate(value.substring(1));
        } catch (error) {
          log.warn(`[RowStore] Inline formula evaluation error: ${value}`, error);
          return undefined;
        }
      }
    }

    return value;
  }

  /**
   * Set value for a column.
   *
   * Mirrors Knockout's RecipeRowVM.createCell():
   * - Updates the observable value
   * - For OverwrittableFormula, marks as manually changed
   * - Pure formulas and startingMaterial columns cannot be overridden
   */
  setValue(columnSymbol: string, value: any): void {
    const column = this.group.getColumn(columnSymbol);
    if (!column) {
      log.warn(`[RowStore] Cannot set value for unknown column ${columnSymbol}`);
      return;
    }

    // Can't override startingMaterial-derived values
    if (column.sourceType === Source.StartingMaterial) {
      log.warn(`[RowStore] Cannot override startingMaterial-derived value: ${columnSymbol}`);
      return;
    }

    // Can't override pure formulas
    if (column.sourceType === Source.Formula) {
      return;
    }

    // Port from Knockout: Format DateTime values as strings before storing
    // Matches Knockout's RecipeRowVM.createCell() behavior
    let valueToStore = value;
    if (column.type === ColumnType.DateTime && typeof value === 'number') {
      valueToStore = formatDateTime(value);
    }

    this.values.set(columnSymbol, valueToStore);

    // Knockout RecipeRowVM.name is backed by the "name" column value.
    if (columnSymbol === 'name') {
      this.name = String(valueToStore ?? '');
    }

    // Clear formula cache when a value changes
    // This invalidates cached formula results so they recalculate
    const hadCachedFormulas = this.formulaCache.size > 0;
    this.formulaCache.clear();

    // Clear formula cache in ALL rows in ALL groups (for cross-group dependencies)
    // This is necessary because formulas can reference other groups via group() function
    this.group.clearAllCaches();

    // Increment cacheVersion AFTER clearing caches, but in a separate action
    // This ensures it doesn't interfere with the save reaction which only tracks row.values
    if (hadCachedFormulas) {
      runInAction(() => {
        this.cacheVersion++;
      });
    }

    // Mark as overridden if it's an overwritable formula
    if (column.sourceType === Source.OverwrittableFormula) {
      this.overrides.set(columnSymbol, true);
    }
  }

  /**
   * Clear all values and overrides in this row.
   * Used when clearing a group to reset template-defined rows.
   */
  clearValues(): void {
    this.values.clear();
    this.overrides.clear();
    this.comment = '';
    this.formulaCache.clear();
    this.cacheVersion++;
  }

  /**
   * Clear manual override and return to formula.
   * Same behavior as Knockout when user resets an overwritten formula.
   */
  clearOverride(columnSymbol: string): void {
    this.overrides.delete(columnSymbol);
    this.values.delete(columnSymbol);
    this.formulaCache.clear(); // Clear cache when reverting to formula
    log.debug(`[RowStore] Cleared override for ${columnSymbol}, returning to formula`);
  }

  setComment(comment: string): void {
    this.comment = comment;
  }

  /**
   * Clear formula cache (called by GroupStore for cross-group invalidation)
   */
  clearFormulaCache(): void {
    this.formulaCache.clear();
    // Increment cache version to trigger MobX reactivity
    this.cacheVersion++;
  }

  /**
   * Calculate a formula for this row
   *
   * Port from Knockout: Format DateTime formula results as strings
   * Matches RecipeRowVM._calc() -> _formatNumber() behavior
   */
  private calculateFormula(column: RecipeColumnState): any {
    if (!column.source) return undefined;

    try {
      // Apply row context to formula scope
      const scope = this.formulaScope.apply(this.group, this);
      // Pass column symbol to exclude it from scope (prevents false circular dep warnings)
      log.debug(`[RowStore] Calculating formula for "${column.symbol}": "${column.source}"`);
      let result = scope.evaluateFormula(column.source, this, column.symbol);
      log.debug(`[RowStore] Formula result for "${column.symbol}":`, result);

      // Port from Knockout: Format results before storing
      // Matches RecipeRowVM._calc() -> _formatNumber() behavior
      if (result !== undefined && result !== null && typeof result === 'number') {
        if (column.type === ColumnType.DateTime) {
          result = formatDateTime(result);
        } else if (column.precision !== undefined) {
          // Apply precision rounding (matches Knockout's math.format with fixed notation)
          result = parseFloat(result.toFixed(column.precision));
        }
      }

      return result;
    } catch (error) {
      log.warn(
        `[RowStore] Error calculating formula for "${column.symbol}":`,
        `Formula: "${column.source}"`,
        `Error:`,
        error
      );
      return undefined;
    }
  }

  /**
   * Get row-specific formula for a FormulaPerRow column.
   * Returns the formula string defined in this row's formulas array.
   */
  private getRowFormula(columnSymbol: string): string | undefined {
    if (!this.formulas || !Array.isArray(this.formulas)) {
      return undefined;
    }

    const formulaEntry = this.formulas.find(([colSymbol]) => colSymbol === columnSymbol);
    return formulaEntry ? formulaEntry[1] : undefined;
  }

  /**
   * Evaluate a row-specific formula (for FormulaPerRow columns)
   *
   * Port from Knockout: Format DateTime results as strings
   */
  private evaluateRowFormula(formula: string, columnSymbol: string): any {
    const column = this.group.getColumn(columnSymbol);

    try {
      const scope = this.formulaScope.apply(this.group, this);
      let result = scope.evaluateFormula(formula, this, columnSymbol);

      // Port from Knockout: Format results before storing
      if (result !== undefined && result !== null && typeof result === 'number') {
        if (column?.type === ColumnType.DateTime) {
          result = formatDateTime(result);
        } else if (column?.precision !== undefined) {
          // Apply precision rounding (matches Knockout's math.format with fixed notation)
          result = parseFloat(result.toFixed(column.precision));
        }
      }

      return result;
    } catch (error) {
      log.warn(
        `[RowStore] Error evaluating row formula for "${columnSymbol}":`,
        `Formula: "${formula}"`,
        `Error:`,
        error
      );
      return undefined;
    }
  }

  /**
   * Resolve row name from the "name" column when present (matches Knockout RecipeRowVM.name getter).
   */
  private resolveName(): string {
    if (this.group.getColumn('name') && this.values.has('name')) {
      return String(this.values.get('name') ?? '');
    }
    return this.name;
  }

  /** Keep row.name aligned with the "name" column after load or programmatic value updates. */
  private syncNameFromValues(): void {
    if (this.group.getColumn('name') && this.values.has('name')) {
      this.name = String(this.values.get('name') ?? '');
    }
  }

  /**
   * Export to plain object for Zustand sync and database save.
   *
   * Includes BOTH raw input values AND calculated formula results (like Knockout).
   * This is what gets saved to the database.
   *
   * NOTE: This is safe to call in reactions because:
   * - Computed values are already cached (lazy creation)
   * - We only access them, not create new ones
   * - The reaction tracks raw value changes, not computed value reads
   */
  toState(): RecipeRowState {
    // Build values map with both raw inputs and calculated formulas
    const allValues = new Map<string, any>();

    // Add all column values (both manual and computed)
    for (const column of this.group.columns) {
      if (column.symbol) {
        // Get value - includes computed formulas (already cached)
        const value = this.getValue(column.symbol);
        if (value !== undefined && value !== null && value !== '') {
          allValues.set(column.symbol, value);
        }
      }
    }

    return {
      id: this.id,
      symbol: this.symbol,
      name: this.resolveName(),
      definedInTemplate: this.definedInTemplate,
      showComment: this.showComment,
      comment: this.comment,
      scrollTo: this.scrollTo,
      formulas: this.formulas, // Include row-specific formulas
      values: allValues, // Includes both raw inputs AND calculated formulas
    };
  }
}
